<template>
  <div id="tooltip_overlay">
    <info-tooltip />
  </div>
  <atus-cps-linking-counts-help />
    
  <atus-header />

  <calendar-grid
      :monthComponent="AtusCpsLinkingCountsMonth"
      :start-year="start_year"
      :end-year="end_year"
      topLeftCellContents="CPS Years"
      captionContents="Total Number of ATUS Respondents by CPS Calendar Month"
      @calendarScroll="onCalendarScroll">
  </calendar-grid>
</template>

<script setup>
import AtusCpsLinkingCountsHelp from "./AtusCpsLinkingCountsHelp.vue";
import AtusCpsLinkingCountsMonth from "./AtusCpsLinkingCountsMonth.vue";
import AtusHeader from "./AtusHeader.vue";
import CalendarGrid from "./CalendarGrid.vue";
import InfoTooltip from "./InfoTooltip.vue";

import {computed, inject, onMounted, provide, readonly, ref} from "vue";
import {
  get_atus_samples,
  get_atus_cps_linking_counts,
  get_cps_supplements,
  makeTooltipData,
  formatInteger, useSelectedSampleTopics
} from "../lib/helpers";
import { useYearMonthPairs } from "../composables/useYearMonthPairs";

const viewing_options = [
  {id: 'last_month', label: '2-5 months prior to ATUS (final month [MIS 8] of CPS)'},
  {id: 'mis5_8', label: '2-8 months prior to ATUS (last four months [MIS 5-8] of CPS)'},
  {id: 'ever', label: '2-20 months prior to ATUS (any month [MIS 1-8] of CPS)'},
];

const tooltips_enabled = ref(true);
const tooltip_data = ref(makeTooltipData());
const cps_supplement_topics = ref({});
const cps_sample_list = ref(null);
const atus_modules = ref({});
const atus_samples = ref({});
const atus_cps_linking_counts = ref({});
const selected_viewing_option = ref(viewing_options[0].id);

const is_loading = inject("is_loading");
const learnMoreUrl = "https://www.atusdata.org/atus/cpslink.shtml";
const learnMoreText = "Learn more about linking ATUS and CPS data";

const years_with_counts_set = computed(() => {
  const yrs = new Set();
  for (const atusSample in atus_cps_linking_counts.value) {
    for (const year in atus_cps_linking_counts.value[atusSample]) {
      yrs.add(year);
    }
  }
  return yrs;
});

const start_year = computed(() => {
  if (years_with_counts_set.value.size > 0) {
    return Math.min(...years_with_counts_set.value.values()) - 1;
  } else {
    return 2010;
  }
});

const end_year = computed(() => {
  if (years_with_counts_set.value.size > 0) {
    return Math.max(...years_with_counts_set.value.values()) + 1;
  } else {
    return 2020;
  }
});

const yearMonthPairs = useYearMonthPairs(atus_samples);

provide('cps_supplement_topics', cps_supplement_topics);
provide('cps_sample_list', cps_sample_list);
provide('atus_modules', atus_modules);
provide('atus_samples', atus_samples);
provide('atus_cps_linking_counts', atus_cps_linking_counts);
provide('viewing_options', readonly(viewing_options));
provide('selected_viewing_option', selected_viewing_option);
provide("learnMoreUrl", learnMoreUrl);
provide("learnMoreText", learnMoreText);
provide("tooltips_enabled", tooltips_enabled);
provide("tooltip_data", tooltip_data);

const selected_samples = computed(() => {
  const s = [];

  for (const [_, sampleEntry] of Object.entries(atus_samples.value)) {
    if (sampleEntry.picked) s.push(sampleEntry.name);
  }

  return s;
});

function getLinkingCounts(year, month) {
  const counts = {};
  const countType = selected_viewing_option.value;
  
  for (const sample of selected_samples.value) {
    const sampleName = `at${sample}`;
    const monthIdx = (month + 1).toString();
    const monthCounts = atus_cps_linking_counts.value[sampleName]?.[year.toString()]?.[monthIdx];
    const c = monthCounts?.[countType] || 0;
    if (c > 0) counts[sample] = formatInteger(c);
  }

  return counts;
}

function getSelectedSampleTopics(year, month) {
  return useSelectedSampleTopics({
    cps_sample_list,
    cps_supplement_topics,
    year: ref(year),
    month: ref(month)
  });
}

const isShowingAnyCounts = computed(() => {
  return yearMonthPairs.value.some(({ year, month }) => {
    const counts = getLinkingCounts(year, month);
    return Object.keys(counts).length > 0;
  });
});

const hasAnySelectedSampleTopics = computed(() => {
  return yearMonthPairs.value.some(({ year, month }) => {
    const topics = getSelectedSampleTopics(year, month);
    return topics.value.length > 0;
  });
});

const shouldShowTooltipsToggle = computed(() => {
  return (
      isShowingAnyCounts.value ||
      hasAnySelectedSampleTopics.value
  );
});

function onCalendarScroll() {
  // Close tooltip on scroll to prevent misalignment
  tooltip_data.value = makeTooltipData({show_tooltip: false});
}

provide("getLinkingCounts", getLinkingCounts);
provide("shouldShowTooltipsToggle", shouldShowTooltipsToggle);
provide("getSelectedSampleTopics", getSelectedSampleTopics);

onMounted(async () => {
  cps_sample_list.value = await get_cps_supplements(cps_supplement_topics, false, false);
  atus_cps_linking_counts.value = await get_atus_cps_linking_counts();
  const [atus_modules_data, atus_samples_data] = await get_atus_samples();
  atus_modules.value = atus_modules_data;
  atus_samples.value = atus_samples_data;
  is_loading.value = false;
});
</script>

<style lang="scss">
  #tooltip_overlay {
    position: fixed;
    inset: 0;
    z-index: 100;
    pointer-events: none;
  }
</style>
